import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

const ACCEPTED = 'video/mp4,video/webm,video/quicktime';
const ALLOWED_MIMES = ['video/mp4', 'video/webm', 'video/quicktime'];
const EXT_TO_MIME = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  qt: 'video/quicktime',
};
const MAX_BYTES = 100 * 1024 * 1024;

const formatMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: '600',
  color: '#555',
  marginBottom: '0.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle = {
  width: '100%',
  padding: '0.55rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const VideoUploadField = ({
  label,
  helperText,
  value,
  onChange,
  bucket = 'artists_videos',
  onUploadingChange,
  hideUrlInput = false,
}) => {
  const { t } = useTranslation('common');
  const fileRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [justUploaded, setJustUploaded] = useState(false);

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const handleUpload = async (file) => {
    setError(null);

    if (file.size > MAX_BYTES) {
      setError(t('uploads.video.sizeError', { size: formatMB(file.size) }));
      return;
    }

    const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
    const contentType = file.type || EXT_TO_MIME[ext] || '';

    if (!ALLOWED_MIMES.includes(contentType)) {
      setError(t('uploads.video.typeError', { type: contentType || t('uploads.video.unknownType') }));
      return;
    }

    const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    setIsUploading(true);
    try {
      const { error: uploadErr } = await supabase.storage
        .from(bucket)
        .upload(path, file, { cacheControl: '3600', upsert: false, contentType });

      if (uploadErr) {
        const detail = uploadErr.error || uploadErr.statusCode || uploadErr.message;
        setError(t('uploads.video.uploadErrorWithDetail', {
          detail: detail || t('uploads.video.unknownType'),
          message: uploadErr.message || '',
        }));
        return;
      }

      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      onChange(data.publicUrl);
      setJustUploaded(true);
    } catch (err) {
      setError(t('uploads.unexpectedError', { detail: err.message }));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <label style={labelStyle}>{label}</label>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={isUploading}
          style={{
            padding: '0.45rem 1rem',
            background: isUploading ? '#f0f0f0' : '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: '600',
            color: isUploading ? '#aaa' : '#555',
            cursor: isUploading ? 'default' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {isUploading ? t('uploads.uploading') : t('uploads.video.upload')}
        </button>
        <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{t('uploads.video.hint')}</span>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
            e.target.value = '';
          }}
        />
      </div>

      {!hideUrlInput && (
        <input
          type="url"
          value={value}
          onChange={(e) => { onChange(e.target.value); setJustUploaded(false); }}
          placeholder={t('uploads.urlPlaceholder')}
          style={{ ...inputStyle, borderColor: isUploading ? '#2ca9e1' : '#ddd' }}
        />
      )}

      {helperText && (
        <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
          {helperText}
        </span>
      )}

      {error && (
        <p style={{ color: '#dc3545', fontSize: '0.82rem', marginTop: '0.4rem', marginBottom: 0 }}>
          {error}
        </p>
      )}

      {value && (
        <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <video
            src={value}
            muted
            loop
            playsInline
            autoPlay
            style={{
              width: 72,
              height: 128,
              objectFit: 'cover',
              borderRadius: 8,
              border: '1px solid #eee',
              background: '#000',
              flexShrink: 0,
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {justUploaded && (
            <span style={{ color: '#28a745', fontSize: '0.82rem', fontWeight: '500' }}>
              {t('uploads.video.success')}
            </span>
          )}
          <button
            type="button"
            onClick={() => { onChange(''); setJustUploaded(false); }}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc3545',
              fontSize: '0.82rem',
              cursor: 'pointer',
              padding: 0,
              marginLeft: 'auto',
            }}
          >
            {t('uploads.video.remove')}
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoUploadField;
